import './applySearchPlatePage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString, toUpper } from 'lodash';
import { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import useApiStore from '../../../store/apiStore/useApiStore';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    delayError: 200,
  });

  const { plate, state, fetchVehicleBy, fetchVehicleFailedMessage } =
    useApplyStore();

  const plateValidation = {
    required: t('validation.plate.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const isPageEnabled = useFeatureFlag(FeatureFlag.ENABLE_PLATE_TO_VIN_SERVICE);

  useEffect(() => {
    // clear any fail message state upon rendering
    useApplyStore.setState({ fetchVehicleFailedMessage: null });

    if (!isPageEnabled) {
      navigate(previous());
    }
  }, [isPageEnabled]);

  const previousStep = (): void => {
    navigate(previous());
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { plateValue, stateValue } = values;

    if (plateValue && stateValue) {
      await fetchVehicleBy({ plate: toUpper(plateValue), state: stateValue });

      if (useApplyStore.getState().fetchVehicleSuccess) {
        navigate(next());
      }
    }
  };

  return (
    <div className="apply-search-plate-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applySearchPlate.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applySearchPlate.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.applySearchPlate.header.drawerButton}
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applySearchPlate.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.applySearchPlate.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applySearchPlate.form.h1}>
                  {t('ApplySearchPlatePage.title')}
                </h1>
                <h2 data-testid={locator.pages.applySearchPlate.form.h2}>
                  {t('ApplySearchPlatePage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <div>
                    <TextField
                      {...register('plateValue', plateValidation)}
                      defaultValue={plate}
                      error={
                        !isEmpty(errors.plateValue) ||
                        !isEmpty(fetchVehicleFailedMessage)
                      }
                      inputProps={{
                        'data-testid':
                          locator.pages.applySearchPlate.form.plateInput,
                      }}
                      label={t('ApplySearchPlatePage.plate')}
                      variant="outlined"
                      fullWidth
                    />

                    {(!isEmpty(errors.plateValue) ||
                      !isEmpty(fetchVehicleFailedMessage)) && (
                      <span
                        data-testid={
                          locator.pages.applySearchPlate.form.plateInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(
                          errors.plateValue?.message ||
                            fetchVehicleFailedMessage,
                        )}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('stateValue', stateValidation)}
                      SelectProps={{
                        IconComponent: ExpandMoreRoundedIcon,
                      }}
                      error={!isEmpty(errors.stateValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.applySearchPlate.form.stateSelect,
                      }}
                      label={t('ApplySearchPlatePage.state')}
                      value={watch('stateValue', state || '')}
                      fullWidth
                      select
                    >
                      <MenuItem value="" disabled>
                        {t('ApplySearchPlatePage.state')}
                      </MenuItem>

                      {USStates.map((aState) => {
                        return (
                          <MenuItem
                            key={aState.abbreviation}
                            value={aState.abbreviation}
                          >
                            {aState.name}
                          </MenuItem>
                        );
                      })}
                    </TextField>

                    {!isEmpty(errors.stateValue) && (
                      <span
                        data-testid={
                          locator.pages.applySearchPlate.form.stateSelectError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.stateValue.message)}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.applySearchPlate.form.submitButton
                    }
                    disabled={!isValid}
                    endIcon={<></>}
                    loading={isLoading}
                    loadingPosition="end"
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {isLoading
                      ? t('ApplySearchPlatePage.searching')
                      : t('ApplySearchPlatePage.continue')}
                  </LoadingButton>
                </div>
              </div>
              <footer data-testid={locator.pages.applySearchPlate.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplySearchPlatePage';
